import * as THREE from "three";
import { Cell } from "@highmarch/domain";
import { cellHash, lib } from "./views/common";
import { FoliageView } from "./views/foliage";
import { bakeStatic } from "./views/staticBatch";
import { FORTRESS_SEAT, GroundContacts } from "./groundSlope";

/** One scenery slot: grid cells along x (`dx`) and z (`dz`) from the fortress (hubCampLayout.ts), and what stands there. */
interface Slot { readonly dx: number; readonly dz: number; readonly kind: "tree" | "rock" | "bush" }

/**
 * The hub's scenery: a sparse ring of trees, rocks and bushes behind and beside the lobby's line-up, in the current
 * biome (FoliageView.biome / Pools), and nothing in front of it — no prop stands between the player and their towers
 * (concept.md, "The 3D scene"). It replaces the level's per-cell dressing, which put ~18 bushes over the hub's cells
 * for 137 k triangles, half of them behind the UI.
 *
 * Static: every prop is baked into a few merged, reduced-detail meshes (staticBatch.ts, lod.ts) that cast no shadow
 * — the ring stands out at the haze, where a tree is a few dozen pixels. Wind-swayed families keep their sway (the
 * shader's), which is the only way the ring moves.
 */
export class HubScenery {
  /** The fortress's contact shadow over its footprint, a little inside the walls (GridView does this on a map). */
  static seatFortress(footprint: readonly Cell[]): void {
    const xs = footprint.map((c) => c.x), zs = footprint.map((c) => c.y);
    const x0 = Math.min(...xs), x1 = Math.max(...xs), z0 = Math.min(...zs), z1 = Math.max(...zs);
    GroundContacts.add({ x: (x0 + x1) / 2, z: (z0 + z1) / 2, hx: (x1 - x0 + 1) / 2 + FORTRESS_SEAT, hz: (z1 - z0 + 1) / 2 + FORTRESS_SEAT, r: 0.6, s: 0.5 });
  }

  readonly group = new THREE.Group();
  private readonly meshes: THREE.Mesh[] = [];

  /**
   * The ring, in grid offsets from the fortress (hubCampLayout.ts): trees outside the war camp's two walls — beyond
   * the north wall either side of the road out, and beyond the east wall down the screen's left — and one on the open
   * right flank. The front stays open: it is under the battle card anyway.
   */
  static readonly Ring: readonly Slot[] = [
    { dx: -5.0, dz: 7.4, kind: "tree" }, { dx: -2.6, dz: 8.1, kind: "tree" }, { dx: 2.8, dz: 6.7, kind: "bush" },
    { dx: 5.6, dz: 8.0, kind: "tree" }, { dx: 8.2, dz: 7.2, kind: "tree" },
    { dx: 8.0, dz: 4.4, kind: "tree" }, { dx: 8.4, dz: 1.6, kind: "rock" }, { dx: 7.9, dz: -1.2, kind: "tree" }, { dx: 7.6, dz: -3.8, kind: "bush" },
    { dx: -6.8, dz: 4.4, kind: "tree" }, { dx: -0.9, dz: 10.6, kind: "tree" }, { dx: 9.6, dz: 10.4, kind: "rock" },
  ];
  /** Detail the ring keeps (lod.ts): a third of each prop, like the level's scenery just past the map edge. */
  static readonly Lod = { ratio: 0.3, error: 0.02 };
  /** Side of a baked chunk, cells: wider than the ring, so it bakes to one mesh per material (a draw each). */
  static readonly Chunk = 32;
  constructor(base: Cell, groundY: number) {
    const L = lib();
    if (!L) return;
    const pool = FoliageView.Pools[FoliageView.biome] ?? FoliageView.Pools.meadow;
    const have = (names: readonly string[]) => names.filter((n) => L.hq(n));
    const byKind = { tree: have(pool.trees), rock: have(pool.rocks), bush: have(pool.dressing.filter((n) => FoliageView.isLow(n, 0.5))) };
    const scratch = new THREE.Group();
    for (const s of HubScenery.Ring) {
      const names = byKind[s.kind].length ? byKind[s.kind] : byKind.tree.length ? byKind.tree : byKind.rock;
      if (names.length === 0) continue;
      const at = { x: base.x + s.dx, z: base.y + s.dz };
      const h = cellHash(new Cell(Math.round(at.x * 7), Math.round(at.z * 7)));
      const name = names[h % names.length];
      const prop = L.clone(`hq:${name}`);
      prop.scale.multiplyScalar(FoliageView.scaleOf(name, prop));
      prop.position.set(at.x, groundY, at.z);
      prop.rotation.y = ((h >> 5) % 8) * (Math.PI / 4);
      scratch.add(prop);
    }
    this.group.add(scratch);
    this.meshes = bakeStatic(scratch, this.group, HubScenery.Chunk, { shadows: false, lod: () => HubScenery.Lod });
    this.group.remove(scratch);
  }

  dispose(): void {
    for (const m of this.meshes) { m.removeFromParent(); m.geometry.dispose(); }
    this.meshes.length = 0;
  }
}
